import { hasCapability, type User } from "../base";
import { NON_EXISTENT_ID } from "../utils/types";

export const remittance = {
  read: (user: User) => hasCapability(user, "remittance.read") || hasCapability(user, "remittance.read.own"),
  // Admin-only, deliberately narrower than `read`, which every internal role holds.
  readInternal: (user: User) => hasCapability(user, "remittance.read.internal"),
  // Held by every internal role; no `read.own` fallback, so contractors read
  // their own schedule but never author notes on it.
  update: (user: User) => hasCapability(user, "remittance.update"),
};

/**
 * Scope shape narrow enough to spread into both `Prisma.BillWhereInput`
 * and `Prisma.VendorCreditWhereInput` (their per-table `StringFilter`
 * generics aren't mutually assignable through the full where input).
 */
export type RemittanceWhereScope = {
  payeeId?: { in: string[] };
  AND?: { id: string }[];
};

/**
 * Defense-in-depth Prisma `where` predicate for remittance reads. The tRPC
 * `permissionFn` is the primary gate; this helper makes the data layer
 * itself enforce scope so a future direct caller (queue, cron, another
 * service) that skips the router can't read past what the user is allowed.
 */
export const remittanceWhere = (user: User, requestedPayeeOrgIds?: string[]): RemittanceWhereScope => {
  // Empty array is a "match no rows" sentinel (the caller asked to scope to
  // zero contractors); never collapse it to "no filter."
  const isEmptyRequest = requestedPayeeOrgIds !== undefined && requestedPayeeOrgIds.length === 0;
  if (isEmptyRequest) {
    return { AND: [{ id: NON_EXISTENT_ID }] };
  }
  if (hasCapability(user, "remittance.read")) {
    if (requestedPayeeOrgIds === undefined) {
      return {};
    }
    return { payeeId: { in: requestedPayeeOrgIds } };
  }
  if (hasCapability(user, "remittance.read.own")) {
    const ownOrgId = user.person.organizationId;
    if (
      requestedPayeeOrgIds === undefined ||
      requestedPayeeOrgIds.length !== 1 ||
      requestedPayeeOrgIds[0] !== ownOrgId
    ) {
      return { AND: [{ id: NON_EXISTENT_ID }] };
    }
    return { payeeId: { in: [ownOrgId] } };
  }
  return { AND: [{ id: NON_EXISTENT_ID }] };
};
